/**
 * The comparators. Pure, and the null rule is the whole file.
 */

/**
 * True when a value carries no measurement.
 *
 * `NaN` counts, and so does a non-empty string that does not parse as a date on
 * a date column — see `unmeasuredDate`. An empty string counts because
 * `Number('')` is 0 and a blank wire value must not become a measured zero.
 */
export function isUnmeasured(v: unknown): boolean {
  if (v === null || v === undefined) return true
  if (typeof v === 'number') return !Number.isFinite(v)
  if (typeof v === 'string') return v.trim() === ''
  return false
}

/**
 * UNMEASURED SORTS LAST IN BOTH DIRECTIONS.
 *
 * ── THIS LOOKS LIKE A BUG AND IS THE POINT ──────────────────────────────────
 * The order is deliberately NOT antisymmetric under a direction flip:
 * `compare(null, x, 'asc') > 0` and `compare(null, x, 'desc') > 0` are both
 * true. A reviewer's instinct is that flipping the direction should flip every
 * pair, and here it must not.
 *
 * The reason is that this app's numbers are quotable. If nulls floated to the
 * top when you sorted ascending by `Costo directo`, the table would state that
 * the UNMEASURED operations are the cheapest — a false claim, manufactured by a
 * sort control, in a screenshot that gets pasted into a meeting. `MarginPage`
 * already prints `sin medir` in that column with the comment "a zero here would
 * claim the work was free"; letting the sort do what the renderer refuses to do
 * would give the number back with extra steps.
 *
 * It is also what the server already does. Three SQL files in this repo write
 * `DESC NULLS LAST`, which is NOT Postgres's default for `DESC` — so somebody
 * paid for this rule once already, deliberately, one layer down.
 */
export function withNullsLast(
  compare: (a: unknown, b: unknown) => number,
): (a: unknown, b: unknown, dir: 'asc' | 'desc') => number {
  return (a, b, dir) => {
    const na = isUnmeasured(a)
    const nb = isUnmeasured(b)
    // Partitioned BEFORE dispatching, so no comparator ever receives a null and
    // no comparator needs to know about them.
    if (na && nb) return 0
    if (na) return 1
    if (nb) return -1
    const r = compare(a, b)
    return dir === 'desc' ? -r : r
  }
}

/**
 * Text, collated.
 *
 * `numeric: true` so `estudio 2` sorts before `estudio 10`, and
 * `sensitivity: 'base'` so `Ángel` and `Angel` are adjacent rather than
 * separated by the whole alphabet — this app's data is Spanish and its
 * organization names are typed by hand.
 */
const collator = new Intl.Collator('es-MX', { numeric: true, sensitivity: 'base' })

export function compareText(a: unknown, b: unknown): number {
  return collator.compare(String(a), String(b))
}

export function compareNumber(a: unknown, b: unknown): number {
  return Number(a) - Number(b)
}

/**
 * ISO timestamps, compared as STRINGS.
 *
 * Not parsed into `Date`: every timestamp in this app is rendered by slicing
 * the ISO string precisely so the viewer's local zone cannot move a purchase
 * into a different month from the invoice that recorded it. Sorting through a
 * local `Date` would reintroduce exactly that, and lexicographic order on
 * fixed-width ISO-8601 is already chronological.
 */
export function compareDate(a: unknown, b: unknown): number {
  return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0
}

export function compareBool(a: unknown, b: unknown): number {
  return Number(Boolean(a)) - Number(Boolean(b))
}

/**
 * A DECLARED domain, ranked by its declaration order.
 *
 * Only for columns whose set of values is written down in this repository —
 * `OrgClass`, the funnel's `RUNGS`, the freshness verdict, open-versus-resolved.
 * A column whose domain is a fact about the product's database (a membership
 * `role`, a raw Stripe status) must use `compareText` instead: ranking by a
 * list this repo invented would silently tie every value it had not heard of,
 * and the header would then render an active sort arrow that reorders nothing.
 */
export function rankedBy(order: readonly string[]): (a: unknown, b: unknown) => number {
  const rank = new Map(order.map((k, i) => [k, i]))
  return (a, b) => {
    const ra = rank.get(String(a))
    const rb = rank.get(String(b))
    // ── AN UNDECLARED VALUE SINKS; IT DOES NOT COLLATE ────────────────────
    // Falling back to collation for any pair TOUCHING an undeclared value,
    // while ranking the rest, is not a total order — it is a cycle. With
    // severities ['error','warn','info'] and one new 'notice':
    //   warn < info  (by rank)
    //   info < notice (by collation)
    //   notice < warn (by collation)
    // `Array.prototype.sort` given an inconsistent comparator produces an
    // arbitrary order, and the observed result was alphabetical with identical
    // severities split into two non-adjacent blocks. That is reachable from a
    // one-line SQL edit: `watch_findings.severity` is free text and
    // `sql/watch/findings.sql` already carries an `ELSE 2` catch-all.
    //
    // Sinking undeclared values is transitive, and it is the same rule this
    // file already applies to the unmeasured ones.
    if (ra === undefined && rb === undefined) return compareText(a, b)
    if (ra === undefined) return 1
    if (rb === undefined) return -1
    return ra - rb
  }
}
